import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  AlertTitle,
  IconButton,
  Skeleton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Header from "../../components/navigation/Header";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  getMarksEvaluation,
  getQuestionPaper,
  postUpdateEvaluation,
} from "../../utils/apiService";
import { useLoader } from "../../components/KenLoader";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../components/KenSnackbar";

type Question = {
  id: string;
  label: string;
  maxMarks: number;
  text?: string;
  isImportant?: boolean;
  isCompulsory?: boolean;
  subQuestions?: {
    id: string;
    label: string;
    maxMarks: number;
  }[];
};

type Section = {
  id: string;
  label: string;
  maxMarks: number;
  description: string;
  chooseBest?: number;
  questions: Question[];
};

type StudentRow = {
  id: number;
  bookletNo: string;
  scores: Record<string, string>;
  examConnectionId?: string;
  examId?: string;
  courseConnectionId?: string;
  courseConnectionRecordId?: string;
};

const SNO_COL_WIDTH = 80;
const BOOKLET_COL_WIDTH = 150;
const TOTAL_COL_WIDTH = 140;
const QUESTION_COL_WIDTH = 120;

// const sectionsData: Section[] = [
//     {
//         id: 'A',
//         label: 'Section A',
//         maxMarks: 15,
//         description: 'All questions will be counted',
//         questions: [
//             {
//                 id: 'Q1',
//                 label: 'Q1',
//                 maxMarks: 5,
//                 text: 'State and explain the principle of duality in electrical networks. Give an example of a dual network.',
//             },
//             {
//                 id: 'Q2',
//                 label: 'Q2',
//                 maxMarks: 5,
//                 text: 'Determine the driving-point impedance of a two-port network represented by the following T-network (give component values). Show the steps clearly.',
//                 subQuestions: [
//                     { id: '1', label: '1', maxMarks: 5 },
//                     { id: '2', label: '2', maxMarks: 5 },
//                     { id: '3', label: '3', maxMarks: 5 },
//                 ],
//             },
//             {
//                 id: 'Q3',
//                 label: 'Q3',
//                 maxMarks: 5,
//                 text: 'State and explain the principle of duality in electrical networks. Give an example of a dual network.',
//                 subQuestions: [
//                     { id: '1', label: '1', maxMarks: 5 },
//                     { id: '2', label: '2', maxMarks: 5 },
//                     { id: '3', label: '3', maxMarks: 5 },
//                 ],
//             },
//         ],
//     },
//     {
//         id: 'B',
//         label: 'Section B',
//         maxMarks: 15,
//         description: 'Any 2 of 3 - best 2 auto-selected',
//         chooseBest: 2,
//         questions: [
//             {
//                 id: 'Q4',
//                 label: 'Q4',
//                 maxMarks: 5,
//                 text: 'State and explain the principle of duality in electrical networks. Give an example of a dual network.',
//                 subQuestions: [
//                     { id: '1', label: '1', maxMarks: 5 },
//                     { id: '2', label: '2', maxMarks: 5 },
//                     { id: '3', label: '3', maxMarks: 5 },
//                 ],
//             },
//             {
//                 id: 'Q5',
//                 label: 'Q5',
//                 maxMarks: 5,
//                 text: 'Determine the driving-point impedance of a two-port network represented by the following T-network (give component values). Show the steps clearly.',
//             },
//             {
//                 id: 'Q6',
//                 label: 'Q6',
//                 maxMarks: 5,
//                 text: 'State and explain the principle of duality in electrical networks. Give an example of a dual network.',
//             },
//         ],
//     },
//     {
//         id: 'C',
//         label: 'Section C',
//         maxMarks: 10,
//         description: 'All questions will be counted',
//         questions: [
//             {
//                 id: 'Q7',
//                 label: 'Q7',
//                 maxMarks: 5,
//                 text: 'State and explain the principle of duality in electrical networks. Give an example of a dual network.',
//             },
//             {
//                 id: 'Q8',
//                 label: 'Q8',
//                 maxMarks: 5,
//                 text: 'Determine the driving-point impedance of a two-port network represented by the following T-network (give component values). Show the steps clearly.',
//             },
//         ],
//     }

// ];

const initialStudents: StudentRow[] = [];

const useStyles = makeStyles(() => ({
  header: {
    fontSize: "18px !important",
    fontWeight: "500 !important",
  },
  container: {
    borderRadius: "8px",
    marginTop: "32px",
    height: "calc(100vh - 165px)",
    width: "100%",
    position: "relative",
    overflowY: "auto",
    overflowX: "hidden",
    background: "#FFF",
  },
  infoCard: {
    borderRadius: 12,
    backgroundColor: "#E9E4EC",
  },
  tableShell: {
    borderRadius: 12,
    border: "1px solid #E0E3EB",
    overflow: "hidden",
    background: "#FFF",
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100% !important",
    "& th, & td": {
      border: "1px solid #E0E3EB",
    },
  },
  scrollArea: {
    overflowX: "auto",
    overflowY: "visible",
    position: "relative",
    width: "100%",
  },
  stickyLeft: {
    position: "sticky",
    zIndex: 10,
    background: "#FFF",
    boxShadow: "inset -1px 0 0 #E0E3EB",
  },
  stickySno: {
    left: 0,
  },
  stickyBooklet: {
    left: SNO_COL_WIDTH,
  },
  stickyRight: {
    position: "sticky",
    right: 0,
    zIndex: 10,
    background: "#FFF",
    boxShadow: "inset 1px 0 0 #E0E3EB",
  },
  headerSection: {
    backgroundColor: "#F4F6FB",
    borderBottom: "1px solid #E0E3EB",
  },
  questionHeader: {
    backgroundColor: "#F4F6FB",
    fontWeight: 600,
    textAlign: "center",
    borderBottom: "1px solid #E0E3EB",
  },
  questionCell: {
    minWidth: QUESTION_COL_WIDTH,
    borderColor: "#E0E3EB",
  },
  totalChip: {
    background: "#E8EBF3 !important",
    fontWeight: "600 !important",
    borderRadius: "999px !important",
    minWidth: 60,
    justifyContent: "center",
  },
  input: {
    width: "100%",
  },
  mobileCard: {
    border: "1px solid #E0E3EB",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    background: "#FFF",
  },
  mobileSectionHeader: {
    marginBottom: 8,
  },
  mobileQuestionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
    borderBottom: "1px solid #E0E3EB",
  },
  mobileQuestionText: {
    flex: 1,
    marginRight: 12,
  },
  mobileMarks: {
    display: "flex",
    alignItems: "center",
    minWidth: 120,
    justifyContent: "flex-end",
    gap: 6,
  },
  mobileWrapper: {
    background: "#F4F5F8",
    minHeight: "100vh",
    padding: "16px",
    paddingBottom: "96px",
  },
  mobileBreadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
    color: "#555A6D",
    fontSize: "13px",
  },
  mobileCourseTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#1F1A3D",
  },
  mobileSubtitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#3C3F4E",
  },
  mobileDescription: {
    fontSize: "13px",
    color: "#4A4F5E",
    lineHeight: 1.5,
  },
  bookletPill: {
    alignSelf: "flex-start",
    padding: "8px 12px",
    background: "#ECE7F4",
    color: "#2E1E58",
    borderRadius: 999,
    fontWeight: 700,
    marginTop: 8,
  },
  attentionCard: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    background: "#F1ECFB",
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
  },
  attentionIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#D4C9F5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2E1E58",
    flexShrink: 0,
  },
  sectionBlock: {
    marginTop: 16,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "15px",
    color: "#1F1A3D",
  },
  sectionHint: {
    fontSize: "13px",
    color: "#555A6D",
    marginBottom: 8,
  },
  sectionCard: {
    border: "1px solid #E0E3EB",
    borderRadius: 12,
    background: "#FFF",
    overflow: "hidden",
  },
  questionRowNew: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 14px",
    borderBottom: "1px solid #EDEEF3",
  },
  questionTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#2E3040",
  },
  questionMeta: {
    fontSize: "12px",
    color: "#61657A",
    marginTop: 4,
  },
  questionMarks: {
    fontWeight: 700,
    fontSize: "14px",
    color: "#2E3040",
    minWidth: 70,
    display: "flex",
    justifyContent: "flex-end",
  },
  marksMuted: {
    color: "#9AA0B5",
  },
  sectionTotal: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 14px",
    background: "#F7F7FB",
    fontWeight: 700,
    color: "#2E3040",
  },
  footerBar: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    padding: "12px 16px",
    background: "rgba(244,245,248,0.95)",
    borderTop: "1px solid #E0E3EB",
    display: "flex",
    gap: 12,
  },
  ghostButton: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 600,
    background: "#EEF0F6",
    color: "#2E3040",
    border: "1px solid #D8DBE6",
    boxShadow: "none",
  },
  primaryButton: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
  },
  pillMuted: {
    background: "#F0F2F7",
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: "12px",
    color: "#5C6070",
  },
  mobileCardShell: {
    background: "#FFF",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 6px 24px rgba(18,38,63,0.08)",
    marginBottom: 12,
  },
  mobileCourseCode: {
    fontWeight: 700,
    fontSize: "16px",
    color: "#1F2430",
  },
  mobileMuted: {
    fontSize: "12px",
    color: "#7A7F8C",
    marginTop: 4,
  },
  totalPill: {
    marginTop: 12,
    background: "#ECEFF5",
    borderRadius: 999,
    padding: "10px 12px",
    textAlign: "center",
    fontWeight: 700,
    color: "#2B3040",
  },
  sectionCardOuter: {
    marginTop: 16,
  },
  questionLabel: {
    fontWeight: 600,
    fontSize: "14px",
    color: "#1F2430",
  },
  errorText: {
    color: "#D32F2F",
    fontSize: "12px",
    marginLeft: 2,
    marginTop: -6,
    marginBottom: 10,
  },
  footerBarAlt: {
    position: "sticky",
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    gap: 12,
    background: "#F7F8FB",
    padding: "12px 8px",
    borderTop: "1px solid #E0E3EB",
  },
  draftButton: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
    background: "#EEF0F6",
    color: "#2E3040",
    border: "1px solid #D8DBE6",
  },
  confirmButton: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
  },
  figmaInput: {
    width: 120,
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: 10,
      backgroundColor: "#F9FAFB",
      "& fieldset": {
        borderColor: "#E4E6EB",
      },
      "&:hover fieldset": {
        borderColor: "#D0D4DD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6C7080",
        boxShadow: "0 0 0 1px #6C7080 inset",
      },
    },
    "& input": {
      textAlign: "right",
      fontWeight: 600,
      color: "#1F2430",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#B1B6C2",
    },
  },
}));

const MarksEvaluation = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [students, setStudents] = useState<StudentRow[]>(initialStudents);
  const [activeBookletIndex, setActiveBookletIndex] = useState<number>(0);
  const { showLoader, hideLoader } = useLoader();
  const [overallResponse, setOverallResponse] = useState<any>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const { state } = useLocation();
  const locationData = state?.data;
  const bundleId = useMemo(() => {
    if (!locationData) return null;
    if (typeof locationData === "string" || typeof locationData === "number") {
      return locationData;
    }
    return (
      locationData?.AssignedBundleID ||
      locationData?.bundleCode ||
      locationData?.bundleId ||
      locationData?.id ||
      null
    );
  }, [locationData]);
  console.log(locationData, "sjdbjd");

  const [data, setData] = useState<any>([]);
  const [totalMarks, setTotalMarks] = useState<number>(0);
  const [questionPaper, setQuestionPaper] = useState<any>();
  const [examConnections, setExamConnections] = useState<any>();
  const [sectionsData, setSectionsData] = useState<Section[]>([]);
  const [questionPaperJson, setQuestionPaperJson] = useState<any>(null);

  const sortSubQuestions = (subQuestions: any[]) => {
    const getName = (item: any) => (item?.sub_question_name ?? "").toString();
    return (subQuestions || [])
      .slice()
      .sort((a, b) =>
        getName(a).localeCompare(getName(b), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
  };

  const getQuestionMaxMarks = (question: any) => {
    const explicit = Number(question?.max_marks);
    if (Number.isFinite(explicit)) {
      return explicit;
    }
    const subs = question?.question_paper_sub_questions || [];
    const sumSubs = subs.reduce((sum: number, sub: any) => {
      const val = Number(sub?.max_marks);
      return sum + (Number.isFinite(val) ? val : 0);
    }, 0);
    return sumSubs;
  };

  const sortQuestions = (questions: any[]) => {
    const parse = (value: any) => {
      const name = (value ?? "").toString();
      const match = name.match(/(\d+)([a-zA-Z]?)/);
      if (!match) return { num: Number.MAX_SAFE_INTEGER, suffix: "" };
      return { num: Number(match[1]), suffix: match[2] || "" };
    };
    return (questions || []).slice().sort((a, b) => {
      const aParts = parse(a?.question_number || a?.id);
      const bParts = parse(b?.question_number || b?.id);
      if (aParts.num !== bParts.num) return aParts.num - bParts.num;
      return aParts.suffix.localeCompare(bParts.suffix, undefined, {
        sensitivity: "base",
      });
    });
  };

  const getQuestionUnits = (section: Section) => {
    const units: {
      key: string;
      label: string;
      maxMarks: number;
      parentId: string;
      parentLabel: string;
      isImportant?: boolean;
    }[] = [];
    section.questions.forEach((q) => {
      const sortedSubs = (q.subQuestions || [])
        .slice()
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
      if (sortedSubs.length > 0) {
        sortedSubs.forEach((sub) => {
          units.push({
            key: `${q.id}-${sub.id}`,
            label: `${q.label}.${sub.label}`,
            maxMarks: sub.maxMarks,
            parentId: q.id,
            parentLabel: q.label,
            isImportant: q.isImportant,
          });
        });
      } else {
        units.push({
          key: q.id,
          label: q.label,
          maxMarks: q.maxMarks,
          parentId: q.id,
          parentLabel: q.label,
          isImportant: q.isImportant,
        });
      }
    });
    return units;
  };

  const getQuestionUnitKeys = (question: Question) => {
    if (question.subQuestions && question.subQuestions.length > 0) {
      return question.subQuestions.map((sub) => `${question.id}-${sub.id}`);
    }
    return [question.id];
  };

  const isQuestionAnswered = (row: StudentRow, question: Question) => {
    return getQuestionUnitKeys(question).some((key) => {
      const val = row.scores[key];
      return val !== undefined && val !== null && val !== "";
    });
  };

  const computeSectionQuestionTotals = (row: StudentRow, section: Section) => {
    return section.questions.map((question) => {
      const unitKeys = getQuestionUnitKeys(question);
      let score = 0;
      unitKeys.forEach((key) => {
        const raw = Number(row.scores[key]);
        if (!Number.isFinite(raw)) return;
        const sub = question.subQuestions?.find(
          (s) => `${question.id}-${s.id}` === key
        );
        const maxAllowed = sub ? sub.maxMarks : question.maxMarks;
        score += Math.min(raw, maxAllowed);
      });
      return { questionId: question.id, score, unitKeys };
    });
  };

  const allQuestionUnits = useMemo(
    () => sectionsData.flatMap((section) => getQuestionUnits(section)),
    [sectionsData]
  );
  const hasSubQuestions = useMemo(
    () =>
      sectionsData.some((section) =>
        section.questions.some(
          (q) => q.subQuestions && q.subQuestions.length > 0
        )
      ),
    [sectionsData]
  );

  useEffect(() => {
    if (!bundleId) {
      showSnackbar(
        "Missing bundle information. Please reopen from Evaluation Requests.",
        "error"
      );
      return;
    }
    showLoader();
    setIsFetching(true);

    getMarksEvaluation(bundleId)
      .then((res) => {
        hideLoader();

        const responseList = res?.data ?? [];

        setExamConnections(responseList?.examConnections);
        setQuestionPaper(responseList?.questionPaper);
        const mappedStudents = (responseList?.examConnections || []).map(
          (item: any, index: number) => {
            let parsedScores: Record<string, string> = {};
            const savedJson = item?.componentScore?.jsonFormat;
            if (savedJson) {
              try {
                const parsed = JSON.parse(savedJson);
                parsedScores = buildScoresFromEvaluationJson(parsed);
              } catch (e) {
                console.error("Failed to parse componentScore jsonFormat", e);
              }
            }
            return {
              id: index + 1,
              bookletNo:
                item?.answerBookletNumber ||
                item?.barcode ||
                item?.bookletId ||
                item?.examConnectionRecordId ||
                `Booklet #${index + 1}`,
              scores: parsedScores,
              examConnectionId:
                item?.examConnectionRecordId || item?.examConnectionId,
              examId: item?.examid || item?.examId,
              courseConnectionRecordId:
                item?.courseConnectionRecordId ||
                item?.courseconnectionrecordid,
            };
          }
        );
        if (mappedStudents.length > 0) {
          setStudents(mappedStudents);
        }

        setExamConnections(responseList?.examConnections);
        const qpJson = responseList?.questionPaper?.jsonFormat;
        if (qpJson) {
          try {
            const parsed = JSON.parse(qpJson);
            setQuestionPaperJson(parsed);
            console.log("Question paper (parsed):", parsed);
            const parts = Array.isArray(parsed)
              ? parsed
              : parsed?.question_paper_parts || [];
            const tempArray = [...parts].sort((a: any, b: any) =>
              (a?.part || "").localeCompare(b?.part || "")
            );
            console.log("Question paper parts (sorted tempArray):", tempArray);

            let sortedArray = tempArray?.map((item: any, index: number) => {
              const importantQuestions = (() => {
                const matches = item?.instructions?.match(/\d+/g);
                return matches ? matches.map(Number) : [];
              })();
              const compulsoryQuestions = importantQuestions;
              return {
                id: item?.part,
                label: `Section ${item?.part}`,
                maxMarks: item?.max_marks,
                chooseBest: Number.isFinite(
                  Number(item?.questions_to_be_answered)
                )
                  ? Number(item?.questions_to_be_answered)
                  : undefined,
                description: item?.instructions,
                questions:
                  sortQuestions(item?.question_paper_questions || []).map(
                    (q: any, qIndex: number) => {
                      const sortedSubQs = sortSubQuestions(
                        q?.question_paper_sub_questions || []
                      );
                      const questionMax = getQuestionMaxMarks(q);
                      const qNumber = Number(q?.question_number);
                      return {
                        id: `Q ${q?.question_number}`,
                        label: `Q ${q?.question_number}`,
                        maxMarks: questionMax,
                        text: "",
                        isImportant:
                          Number.isFinite(qNumber) &&
                          importantQuestions.includes(qNumber),
                        isCompulsory:
                          Number.isFinite(qNumber) &&
                          compulsoryQuestions.includes(qNumber),
                        subQuestions:
                          sortedSubQs.map((sub: any, subIndex: number) => {
                            return {
                              id: sub?.sub_question_name,
                              label: sub?.sub_question_name,
                              maxMarks: sub?.max_marks,
                            };
                          }) || [],
                      };
                    }
                  ) || [],
              };
            });

            setSectionsData(sortedArray || []);
          } catch (error) {
            console.log("Question paper (raw jsonFormat):", qpJson);
            console.error("Question paper parse error", error);
          }
        }

        console.log(responseList, "sdscskhbc");
      })
      .catch((err) => {
        hideLoader();
        console.error(err);
      })
      .finally(() => setIsFetching(false));
  }, [bundleId]);

  useEffect(() => {
    if (students.length === 0) return;
    if (activeBookletIndex > students.length - 1) {
      setActiveBookletIndex(0);
    }
  }, [students.length, activeBookletIndex]);

  const activeBooklet = students[activeBookletIndex];

  const tableMinWidth = useMemo(
    () =>
      SNO_COL_WIDTH +
      BOOKLET_COL_WIDTH +
      TOTAL_COL_WIDTH +
      sectionsData.reduce((sum, section) => {
        return sum + getQuestionUnits(section).length * QUESTION_COL_WIDTH;
      }, 0),
    [sectionsData]
  );

  const totalPossibleMarks = useMemo(
    () => sectionsData.reduce((sum, section) => sum + section.maxMarks, 0),
    [sectionsData]
  );

  const bestSelections = useMemo(() => {
    const selections: Record<number, Set<string>> = {};
    students.forEach((row) => {
      selections[row.id] = new Set<string>();
      sectionsData.forEach((section) => {
        if (!section.chooseBest) return;
        const questionLookup = new Map(section.questions.map((q) => [q.id, q]));
        const questionTotals = computeSectionQuestionTotals(row, section);

        const compulsoryTotals = questionTotals.filter((qt) => {
          const meta = questionLookup.get(qt.questionId);
          return meta?.isCompulsory && isQuestionAnswered(row, meta);
        });

        const optionalTotals = questionTotals.filter((qt) => {
          const meta = questionLookup.get(qt.questionId);
          return meta && !meta.isCompulsory && isQuestionAnswered(row, meta);
        });

        const remainingSlots = Math.max(
          section.chooseBest - compulsoryTotals.length,
          0
        );
        const selectedOptional = optionalTotals
          .slice()
          .sort((a, b) => b.score - a.score)
          .slice(0, remainingSlots);

        [...compulsoryTotals, ...selectedOptional].forEach((q) =>
          q.unitKeys.forEach((key) => selections[row.id].add(key))
        );
      });
    });
    return selections;
  }, [students, sectionsData]);

  const getSelectedQuestionTotals = (row: StudentRow, section: Section) => {
    if (!section.chooseBest) return computeSectionQuestionTotals(row, section);
    const questionLookup = new Map(section.questions.map((q) => [q.id, q]));
    const questionTotals = computeSectionQuestionTotals(row, section);

    const compulsoryTotals = questionTotals.filter((qt) => {
      const meta = questionLookup.get(qt.questionId);
      return meta?.isCompulsory && isQuestionAnswered(row, meta);
    });

    const optionalTotals = questionTotals.filter((qt) => {
      const meta = questionLookup.get(qt.questionId);
      return meta && !meta.isCompulsory && isQuestionAnswered(row, meta);
    });

    const remainingSlots = Math.max(
      section.chooseBest - compulsoryTotals.length,
      0
    );
    const selectedOptional = optionalTotals
      .slice()
      .sort((a, b) => b.score - a.score)
      .slice(0, remainingSlots);

    return [...compulsoryTotals, ...selectedOptional];
  };

  const errorSet = useMemo(() => {
    const errs = new Set<string>();
    students.forEach((row) => {
      sectionsData.forEach((section) => {
        getQuestionUnits(section).forEach((unit) => {
          const val = Number(row.scores[unit.key]);
          if (Number.isFinite(val) && val > unit.maxMarks) {
            errs.add(`${row.id}-${unit.key}`);
          }
        });
      });
    });
    return errs;
  }, [students, sectionsData]);

  const answerLimitErrors = useMemo(
    () => new Set<string>(),
    [students, sectionsData]
  );

  const hasErrors = errorSet.size > 0;

  const calculateRowTotal = (row: StudentRow) => {
    return sectionsData.reduce(
      (sum, section) => sum + calculateSectionTotal(row, section),
      0
    );
  };

  const calculateSectionTotal = (row: StudentRow, section: Section) => {
    if (!section.chooseBest) {
      const questionTotals = computeSectionQuestionTotals(row, section);
      return questionTotals.reduce((acc, curr) => acc + curr.score, 0);
    }

    const selected = getSelectedQuestionTotals(row, section);
    return selected.reduce((acc, curr) => acc + curr.score, 0);
  };

  const buildEvaluationJsonForRow = (row: StudentRow) => {
    if (!questionPaperJson) return null;
    const cloned = JSON.parse(JSON.stringify(questionPaperJson));
    const parts = Array.isArray(cloned) ? cloned : cloned?.question_paper_parts;
    if (!parts) return null;

    const partsWithScores = parts.map((part: any) => {
      const compulsory = (() => {
        const matches = part?.instructions?.match(/\d+/g);
        return matches ? matches.map(Number) : [];
      })();
      const questions = (part?.question_paper_questions || []).map((q: any) => {
        const qKey = `Q ${q?.question_number}`;
        const isCompulsory =
          Number.isFinite(Number(q?.question_number)) &&
          compulsory.includes(Number(q?.question_number));
        const updated: any = { ...q, is_compulsory: isCompulsory };
        if (q?.question_paper_sub_questions?.length) {
          updated.question_paper_sub_questions =
            q.question_paper_sub_questions.map((sub: any) => {
              const subKey = `${qKey}-${sub?.sub_question_name}`;
              const score = Number(row.scores[subKey]);
              return { ...sub, score: Number.isFinite(score) ? score : 0 };
            });
        } else {
          const score = Number(row.scores[qKey]);
          updated.score = Number.isFinite(score) ? score : 0;
        }
        return updated;
      });
      return { ...part, question_paper_questions: questions };
    });

    if (Array.isArray(cloned)) {
      return partsWithScores;
    }
    return { ...cloned, question_paper_parts: partsWithScores };
  };

  const buildScoresFromEvaluationJson = (evaluationJson: any) => {
    const scores: Record<string, string> = {};
    const parts = Array.isArray(evaluationJson)
      ? evaluationJson
      : evaluationJson?.question_paper_parts || [];
    parts.forEach((part: any) => {
      (part?.question_paper_questions || []).forEach((q: any) => {
        const baseKey = `Q ${q?.question_number}`;
        const subQs = q?.question_paper_sub_questions || [];
        if (subQs.length > 0) {
          subQs.forEach((sub: any) => {
            const key = `${baseKey}-${sub?.sub_question_name}`;
            const score = Number(sub?.score ?? sub?.marks);
            if (Number.isFinite(score) && score !== 0) {
              scores[key] = score.toString();
            }
          });
        } else {
          const score = Number(q?.score ?? q?.marks);
          if (Number.isFinite(score) && score !== 0) {
            scores[baseKey] = score.toString();
          }
        }
      });
    });
    return scores;
  };

  const handleSave = async (status: "draft" | "submit" = "draft") => {
    if (!questionPaperJson) {
      showSnackbar("Question paper not loaded yet.", "error");
      return;
    }
    if (hasErrors) {
      showSnackbar("Please fix validation issues before saving.", "error");
      return;
    }
    showLoader();
    try {
      const contactId = localStorage.getItem("contactId");
      const payloads = students
        .map((row, index) => {
          const evaluationJson = buildEvaluationJsonForRow(row);

          console.log(evaluationJson, "lkscmsdcnsk");

          if (!evaluationJson) return null;
          const connection =
            (examConnections || []).find(
              (conn: any) =>
                conn?.examConnectionRecordId === row?.examConnectionId
            ) || (examConnections || [])[index];
          const componentScore = connection?.componentScore || {};
          const savedScores = componentScore?.jsonFormat
            ? (() => {
                try {
                  const parsed = JSON.parse(componentScore.jsonFormat);
                  return buildScoresFromEvaluationJson(parsed);
                } catch {
                  return {};
                }
              })()
            : {};
          const evaluationJsonString = JSON.stringify(evaluationJson);
          const totalMarks = calculateRowTotal(row);
          const draftStatus =
            totalMarks > 0 ? "Partially evaluated" : "Pending";
          const payload = {
            contactId: contactId,
            examFormat:
              evaluationJson?.question_paper_format ||
              questionPaper?.examFormat ||
              locationData?.ExamFormat,
            courseCode:
              connection?.courseCode ||
              questionPaper?.courseCode ||
              locationData?.courseCode,
            examType: questionPaper?.examType || locationData?.ExamType,
            courseConnectionId: row?.courseConnectionRecordId,
            evaluatorType:
              localStorage.getItem("evaluationType") ||
              questionPaper?.evaluationType ||
              locationData?.evaluationType,
            evaluationJSON: evaluationJsonString,
            evaluationStatus:
              status === "submit" ? "Completed" : draftStatus,
            componentScoresId: componentScore?.ComponentscoreId || null,
            subComponentScoresId: componentScore?.SubComponentscoreId || null,
            examId:
              row?.examId || connection?.examRecordId || questionPaper?.examId,
            examConnectionId:
              row?.examConnectionId || connection?.examConnectionRecordId,
            subComponentMarksObtained: totalMarks,
          };
          return payload;
        })
        .filter(Boolean) as any[];

      console.log(JSON.stringify(payloads, null, 2), "ldkfnslnfcldb");
      const response = await postUpdateEvaluation(payloads);
      setOverallResponse(response);

      const isSuccess =
        response?.success === true || response?.success === "true";
      if (!isSuccess) {
        const messages = Array.isArray(response?.errorMessages)
          ? response.errorMessages
              .map((err: any) => err?.errorMessage || err?.message || err)
              .filter(Boolean)
          : [];
        const errorText =
          messages.length > 0
            ? messages.join(" | ")
            : response?.message || "Failed to save evaluation.";
        showSnackbar(errorText, "error");
        return;
      }

      showSnackbar("Evaluation saved successfully.", "success");
      const navigationState =
        typeof locationData === "string" || typeof locationData === "number"
          ? { AssignedBundleID: locationData }
          : locationData;
      if (navigationState) {
        navigate("/faculty/evaluationRequests", {
          state: { data: navigationState },
        });
      } else {
        navigate("/faculty/evaluationRequests");
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Failed to save evaluation. Please try again.", "error");
    } finally {
      hideLoader();
    }
  };

  const formatScoreDisplay = (
    row: StudentRow | undefined,
    unitKey: string,
    max: number
  ) => {
    if (!row) return "--";
    const parsed = Number(row.scores[unitKey]);
    if (!Number.isFinite(parsed)) return "--";
    const capped = Math.min(parsed, max);
    return capped.toString().padStart(2, "0");
  };

  const bookletTotal = activeBooklet ? calculateRowTotal(activeBooklet) : 0;
  const isLastBooklet = activeBookletIndex === students.length - 1;
  const bundleTitle =
    locationData?.title ||
    locationData?.courseName ||
    "Network Analysis & Synthesis";
  const bookletLabel =
    activeBooklet?.bookletNo || `Booklet #${activeBookletIndex + 1}`;

  const renderSkeleton = () => {
    const rows = Array.from({ length: 4 });
    return (
      <div>
        <Header title={questionPaper?.courseName || "Loading..."} />
        <Box
          component={Paper}
          className={classes.container}
          sx={{ p: 0, mt: "32px" }}
        >
          <Box
            p={3}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Box>
              <Skeleton variant="text" width={240} height={32} />
              <Skeleton variant="text" width={180} height={20} />
            </Box>
            <Box className="d-flex gap-3 align-items-center">
              <Skeleton variant="rectangular" width={120} height={36} />
              <Skeleton variant="rectangular" width={90} height={36} />
            </Box>
          </Box>
          <Divider />
          <Box p={3}>
            <Skeleton
              variant="rectangular"
              height={72}
              sx={{ borderRadius: 2, mb: 3 }}
            />
            <TableContainer className={classes.scrollArea}>
              <Table stickyHeader className={classes.table}>
                <TableHead>
                  <TableRow>
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <TableCell key={idx}>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((_, rIdx) => (
                    <TableRow key={rIdx}>
                      {Array.from({ length: 6 }).map((_, cIdx) => (
                        <TableCell key={cIdx}>
                          <Skeleton variant="rectangular" height={36} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </div>
    );
  };

  const handleScoreChange = (
    rowId: number,
    questionId: string,
    value: string
  ) => {
    const numericOnly = value.replace(/\D/g, "");
    const cleaned =
      numericOnly === ""
        ? ""
        : (() => {
            const parsed = Number(numericOnly);
            if (!Number.isFinite(parsed)) return "";
            return parsed.toString();
          })();

    setStudents((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? { ...row, scores: { ...row.scores, [questionId]: cleaned } }
          : row
      )
    );
  };

  const handleConfirmNext = () => {
    if (students.length === 0) return;
    setActiveBookletIndex((prev) => (prev + 1) % students.length);
  };

  if (isMobile) {
    if (isFetching) return renderSkeleton();
    return (
      <div>
        <Header title={bundleTitle} />
        <Box className={classes.mobileWrapper}>
          <Box className={classes.mobileBreadcrumb} mb={1}>
            <IconButton size="small" onClick={() => navigate(-1)}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography className={classes.mobileCourseTitle}>
              {bundleTitle}
            </Typography>
            {students.length > 0 && (
              <Box className={classes.pillMuted}>
                {activeBookletIndex + 1} of {students.length}
              </Box>
            )}
          </Box>

          <Box className={classes.mobileCardShell}>
            <Typography className={classes.mobileCourseCode}>
              {bookletLabel}
            </Typography>
            <Typography className={classes.mobileMuted}>
              Last saved 3 minutes ago
            </Typography>
            <Box className={classes.totalPill}>
              Total marks: {bookletTotal.toString().padStart(2, "0")}/
              {totalPossibleMarks}
            </Box>
          </Box>

          {sectionsData.map((section) => {
            const sectionTotal = activeBooklet
              ? calculateSectionTotal(activeBooklet, section)
              : 0;
            return (
              <Box key={section.id} className={classes.sectionCardOuter}>
                <Typography className={classes.sectionTitle}>
                  {section.label} - {section.maxMarks} marks
                </Typography>
                <Typography className={classes.sectionHint}>
                  {section.description}
                </Typography>
                <Box className={classes.sectionCard}>
                  {getQuestionUnits(section).map((unit, idx, arr) => {
                    const isError = activeBooklet
                      ? errorSet.has(`${activeBooklet.id}-${unit.key}`)
                      : false;
                    const rawVal = activeBooklet?.scores?.[unit.key];
                    const hasValue =
                      rawVal !== undefined && rawVal !== null && rawVal !== "";
                    const isSelected =
                      hasValue && section?.chooseBest && activeBooklet
                        ? bestSelections[activeBooklet.id]?.has(unit.key)
                        : false;
                    const isLast = idx === arr.length - 1;
                    const value = activeBooklet?.scores?.[unit.key] ?? "";
                    const labelWithStar = unit.isImportant ? (
                      <Box component="span">
                        {unit.label}
                        <Box component="span" sx={{ color: "#d32f2f" }}>
                          {" "}
                          *
                        </Box>
                      </Box>
                    ) : (
                      unit.label
                    );

                    return (
                      <React.Fragment key={unit.key}>
                        <Box
                          className={classes.questionRowNew}
                          sx={{
                            borderBottom: isLast ? "none" : "1px solid #EDEEF3",
                          }}
                        >
                          <Typography className={classes.questionLabel}>
                            {labelWithStar} ({unit.maxMarks})
                          </Typography>
                          <TextField
                            size="small"
                            variant="outlined"
                            className={classes.figmaInput}
                            placeholder="--"
                            value={value}
                            error={isError}
                            onChange={(event) =>
                              activeBooklet &&
                              handleScoreChange(
                                activeBooklet.id,
                                unit.key,
                                event.target.value
                              )
                            }
                            inputProps={{
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              min: 0,
                              max: unit.maxMarks,
                              style: { textAlign: "right" },
                            }}
                            InputProps={{
                              endAdornment: isError ? (
                                <InputAdornment position="end">
                                  <ErrorOutlineIcon
                                    fontSize="small"
                                    color="error"
                                  />
                                </InputAdornment>
                              ) : isSelected ? (
                                <InputAdornment position="end">
                                  <CheckCircleOutlinedIcon
                                    fontSize="small"
                                    color="success"
                                  />
                                </InputAdornment>
                              ) : undefined,
                            }}
                          />
                        </Box>
                        {isError && (
                          <Typography className={classes.errorText}>
                            Max allowed is {unit.maxMarks}
                          </Typography>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <Box className={classes.sectionTotal}>
                    <Typography>Total marks</Typography>
                    <Typography>
                      {sectionTotal}/{section.maxMarks}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}

          <Box height={32} />
          <Box className={classes.footerBarAlt}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => handleSave("draft")}
            >
              Save as Draft
            </Button>
            <Button
              variant="contained"
              color="secondary"
              // className={classes.confirmButton}
              onClick={() => {
                if (isLastBooklet) {
                  handleSave("draft");
                } else {
                  handleConfirmNext();
                }
              }}
            >
              Confirm &amp; Next Booklet
            </Button>
          </Box>
        </Box>
      </div>
    );
  }

  if (isFetching) {
    return renderSkeleton();
  }

  return (
    <div>
      <Header title={questionPaper?.courseName} />
      <Box
        component={Paper}
        className={classes.container}
        sx={{
          p: 0,
          mt: "32px",
          backgroundColor: "#FFF",
          height: "calc(100vh - 165px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Box
          p={3}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Box>
            <Typography className={classes.header}>
              {questionPaper?.courseName} | {questionPaper?.courseCode}
            </Typography>
            <Typography>
              {questionPaper?.examConnectionCount} Booklets |{" "}
              {questionPaper?.examFormat.split(";").join(" , ")}
            </Typography>
          </Box>
          <Box className="d-flex gap-3 align-items-center">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => handleSave("draft")}
            >
              Save as draft
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSave("submit")}
            >
              Submit
            </Button>
          </Box>
        </Box>
        <Divider />

        <Box className={classes.infoCard} p={3} m={3}>
          <Typography fontWeight={600}>Please note!</Typography>
          <Typography fontSize={"14px"} color="text.secondary">
            If you leave a field blank, it will automatically be counted as 0.
          </Typography>
        </Box>
        {hasErrors && (
          <Box px={3} pb={2}>
            <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
              <AlertTitle>Validation issues</AlertTitle>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                Some questions have marks higher than the allowed maximum.
              </Typography>
            </Alert>
          </Box>
        )}

        <Box px={3} pb={3} sx={{ width: "calc(100vw - 320px)" }}>
          <TableContainer className={classes.scrollArea}>
            <Table
              stickyHeader
              className={classes.table}
              sx={{ minWidth: tableMinWidth }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    className={`${classes.stickyLeft} ${classes.stickySno} ${classes.headerSection}`}
                    rowSpan={hasSubQuestions ? 3 : 2}
                    sx={{ minWidth: SNO_COL_WIDTH, fontWeight: 600 }}
                  >
                    S No
                  </TableCell>
                  <TableCell
                    className={`${classes.stickyLeft} ${classes.stickyBooklet} ${classes.headerSection}`}
                    rowSpan={hasSubQuestions ? 3 : 2}
                    sx={{ minWidth: BOOKLET_COL_WIDTH, fontWeight: 600 }}
                  >
                    Barcode Number
                  </TableCell>
                  {sectionsData.map((section) => (
                    <TableCell
                      key={section.id}
                      align="center"
                      colSpan={getQuestionUnits(section).length}
                      className={classes.headerSection}
                      sx={{
                        borderLeft: "1px solid #E0E3EB",
                        borderRight: "1px solid #E0E3EB",
                        zIndex: 0,
                      }}
                    >
                      <Typography fontWeight={600}>
                        {section.label} - {section.maxMarks} marks
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {section.description}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell
                    className={`${classes.stickyRight} ${classes.headerSection}`}
                    rowSpan={hasSubQuestions ? 3 : 2}
                    align="center"
                    sx={{ minWidth: TOTAL_COL_WIDTH, fontWeight: 600 }}
                  >
                    Total Marks ({totalPossibleMarks})
                  </TableCell>
                </TableRow>
                <TableRow>
                  {sectionsData.map((section) =>
                    section.questions.map((question) => {
                      const subCount = question.subQuestions?.length || 0;
                      const labelWithStar = question.isImportant ? (
                        <Box component="span">
                          {question.label}
                          <Box component="span" sx={{ color: "#d32f2f" }}>
                            {" "}
                            *
                          </Box>
                        </Box>
                      ) : (
                        question.label
                      );
                      return (
                        <TableCell
                          key={question.id}
                          className={classes.questionHeader}
                          sx={{ zIndex: 0 }}
                          colSpan={subCount > 0 ? subCount : 1}
                        >
                          {labelWithStar} ({question.maxMarks})
                        </TableCell>
                      );
                    })
                  )}
                </TableRow>
                {hasSubQuestions && (
                  <TableRow>
                    {sectionsData.map((section) =>
                      section.questions.map((question) => {
                        const subQuestions = (question.subQuestions || [])
                          .slice()
                          .sort((a, b) =>
                            a.label.localeCompare(b.label, undefined, {
                              numeric: true,
                              sensitivity: "base",
                            })
                          );
                        if (subQuestions.length === 0) {
                          return (
                            <TableCell
                              key={`${question.id}-empty`}
                              className={classes.questionHeader}
                            />
                          );
                        }
                        return subQuestions.map((sub) => (
                          <TableCell
                            key={`${question.id}-${sub.id}`}
                            className={classes.questionHeader}
                            sx={{ zIndex: 0 }}
                          >
                            {question.label}.{sub.label} ({sub.maxMarks})
                          </TableCell>
                        ));
                      })
                    )}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {students.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell
                      className={`${classes.stickyLeft} ${classes.stickySno}`}
                      sx={{ minWidth: SNO_COL_WIDTH, borderColor: "#E0E3EB" }}
                    >
                      {row.id}
                    </TableCell>
                    <TableCell
                      className={`${classes.stickyLeft} ${classes.stickyBooklet}`}
                      sx={{
                        minWidth: BOOKLET_COL_WIDTH,
                        borderColor: "#E0E3EB",
                      }}
                    >
                      {row.bookletNo}
                    </TableCell>
                    {sectionsData.map((section) =>
                      getQuestionUnits(section).map((unit) => {
                        const isError = errorSet.has(`${row.id}-${unit.key}`);
                        const rawVal = row.scores[unit.key];
                        const hasValue =
                          rawVal !== undefined &&
                          rawVal !== null &&
                          rawVal !== "";
                        const isSelected =
                          hasValue &&
                          section?.chooseBest &&
                          bestSelections[row.id]?.has(unit.key);
                        return (
                          <TableCell
                            key={`${row.id}-${unit.key}`}
                            className={classes.questionCell}
                            align="center"
                          >
                            <TextField
                              size="small"
                              className={classes.input}
                              value={row.scores[unit.key] ?? ""}
                              error={isError}
                              onChange={(event) =>
                                handleScoreChange(
                                  row.id,
                                  unit.key,
                                  event.target.value
                                )
                              }
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                min: 0,
                                max: unit.maxMarks,
                                style: { textAlign: "center" },
                              }}
                              InputProps={{
                                endAdornment: isError ? (
                                  <InputAdornment position="end">
                                    <ErrorOutlineIcon
                                      fontSize="small"
                                      color="error"
                                    />
                                  </InputAdornment>
                                ) : isSelected ? (
                                  <InputAdornment position="end">
                                    <CheckCircleOutlinedIcon
                                      fontSize="small"
                                      color="success"
                                    />
                                  </InputAdornment>
                                ) : undefined,
                              }}
                            />
                          </TableCell>
                        );
                      })
                    )}
                    <TableCell
                      className={classes.stickyRight}
                      sx={{ minWidth: TOTAL_COL_WIDTH, borderColor: "#E0E3EB" }}
                      align="center"
                    >
                      <Chip
                        label={calculateRowTotal(row)
                          .toString()
                          .padStart(2, "0")}
                        className={classes.totalChip}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </div>
  );
};

export default MarksEvaluation;
